import '@gl/lit/components/base/floating_surface_v2/index.js';
import '@gl/lit/components/base/floating_surface_list/index.js';
import '@gl/lit/components/base/floating_surface_list_item/index.js';
import '@gl/lit/components/base/icon/index.js';

import {html, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import type {Ref} from 'lit/directives/ref.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {when} from 'lit/directives/when.js';
import {Size as IconSize} from '@gl/lit/components/base/icon/index.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import styles from './style.scss';
import {isHTMLElement} from '@gl/lit/utils/dom.js';
import type FlyListItem from '@gl/lit/components/base/list_item/index.js';
import {DropdownPlacement} from '@gl/lit/components/base/floating_surface_v2/index.js';

const SPLIT_BUTTON_ELEMENT_TAG = 'split-button-options';

/**
 * SplitButton is a custom Web Component that serves as a split button for node options.
 * @property {Boolean} visible - Determines the visibility of the button.
 * @property {String} [placement=DropdownPlacement.BOTTOM] - Describes where to place the button ('bottom' by default).
 * @property {IconSize} [size=IconSize.SM] - Define the icon size ('small' by default).
 * @property {Ref<HTMLButtonElement>} btnRef - A reference to the button element.
 */
@customElement(SPLIT_BUTTON_ELEMENT_TAG)
export default class SplitButton extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles];
  }

  @property({type: Boolean})
  visible = false;

  @property({type: String})
  placement: DropdownPlacement = DropdownPlacement.BOTTOM;

  @property({type: String})
  size = IconSize.SM;

  @property({type: String})
  text = '';

  @property({type: String})
  icon = 'more_vert';

  btnRef: Ref<HTMLButtonElement> = createRef();

  render() {
    return html`
      <div class="wrapper">
        <button
          ${ref(this.btnRef)}
          type="button"
          label="Node options"
          class="node-options"
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            this.handleUpdate(Msg.toggle());
          }}
        >
          <div class="split-button__text">${this.text}</div>
          <fly-icon size=${this.size} icon=${this.icon}></fly-icon>
        </button>
        ${when(
          this.btnRef,
          () => html`
            <fly-floating-surface-v2
              placement=${this.placement}
              closeWhenClickOutside
              .anchor=${this.btnRef.value ?? null}
              ?visible=${this.model.visible}
              @close=${() => this.handleUpdate(Msg.close)}
              @open=${() => this.handleUpdate(Msg.open)}
              @click=${(event: MouseEvent) => {
                event.stopPropagation();
              }}
            >
              <fly-list>
                <slot
                  @click=${(event: MouseEvent) => {
                    for (const el of event.composedPath()) {
                      if (!isHTMLElement(el)) continue;
                      if (el.tagName.toLocaleLowerCase() === 'fly-list-item') {
                        (el as FlyListItem).selected = false;
                        this.handleUpdate(Msg.close());
                        return;
                      }
                    }
                  }}
                ></slot>
              </fly-list>
            </fly-floating-surface-v2>
          `,
          () => nothing
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SPLIT_BUTTON_ELEMENT_TAG]: SplitButton;
  }
}
